import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FILTER_REGISTRY, getFilterById, UNASSIGNED_ASSIGNEE_CODE } from './filterRegistry';
import { ParseContext } from './types';

export interface FilterValueMap {
    [filterId: string]: unknown;
}

export interface AppliedChipData {
    id: string;
    label: string;
}

export interface UseFiltersResult {
    picked: Set<string>;
    pending: FilterValueMap;
    applied: FilterValueMap;
    appliedCount: number;
    appliedChips: AppliedChipData[];
    getApplied: <T>(id: string) => T;
    getPending: <T>(id: string) => T;
    setPending: (id: string, value: unknown) => void;
    togglePicked: (id: string) => void;
    apply: () => void;
    reset: () => void;
    removeApplied: (id: string) => void;
}

function buildDefaultMap(): FilterValueMap {
    return Object.fromEntries(FILTER_REGISTRY.map((f) => [f.id, f.defaultValue]));
}

// Assignee options arrive asynchronously from useAssigneesQuery. Defer URL init
// until they resolve so ?assignee=email round-trips correctly on a cold load.
// Unassigned is a static option, so ?assignee=unassigned alone does not wait.
function isCtxReadyFor(params: URLSearchParams, ctx: ParseContext): boolean {
    const hasAssigneeEmailParam = params
        .getAll('assignee')
        .some((code) => code !== UNASSIGNED_ASSIGNEE_CODE);
    return !hasAssigneeEmailParam || ctx.assigneeOptions.length > 0;
}

export function useFilters(ctx: ParseContext): UseFiltersResult {
    const [searchParams, setSearchParams] = useSearchParams();
    const hasInitialized = useRef(false);

    const [picked, setPicked] = useState<Set<string>>(() => new Set());
    const [pending, setPendingState] = useState<FilterValueMap>(buildDefaultMap);
    const [appliedValues, setAppliedValues] = useState<FilterValueMap>(buildDefaultMap);

    // URL → state init. Runs once, but waits for ctx to be ready so assignee
    // (which depends on async-loaded options) parses correctly.
    useEffect(() => {
        if (hasInitialized.current) return;
        if (!isCtxReadyFor(searchParams, ctx)) return;
        hasInitialized.current = true;

        const hasAnyParam = FILTER_REGISTRY.some((f) =>
            (f.urlKeys ?? [f.urlKey]).some((k) => searchParams.get(k) !== null),
        );
        if (!hasAnyParam) return;

        const parsed: FilterValueMap = {};
        const initialPicked = new Set<string>();

        FILTER_REGISTRY.forEach((f) => {
            const value = f.parseFromParams(searchParams, ctx);
            parsed[f.id] = value;
            if (f.isApplied(value)) initialPicked.add(f.id);
        });

        // conflictsWith is authoritative for mutual exclusion. If a URL arrives
        // with both sides of a conflict set (e.g. ?status=1&reviewType=2), drop
        // the conflicting filter so we never send conflicting query params to
        // the backend. Resolution walks the registry in reverse so the
        // later-declared filter wins — this preserves the legacy rule that
        // reviewType overrides status/product (ORIG-607).
        for (let i = FILTER_REGISTRY.length - 1; i >= 0; i--) {
            const f = FILTER_REGISTRY[i];
            if (!initialPicked.has(f.id)) continue;
            (f.conflictsWith ?? []).forEach((id) => {
                if (initialPicked.has(id)) {
                    initialPicked.delete(id);
                    const conflicting = getFilterById(id);
                    if (conflicting) parsed[id] = conflicting.defaultValue;
                }
            });
        }

        setPicked(initialPicked);
        setPendingState(parsed);
        setAppliedValues(parsed);
    }, [ctx, searchParams]);

    const appliedChips = useMemo<AppliedChipData[]>(
        () =>
            FILTER_REGISTRY.filter((f) => f.isApplied(appliedValues[f.id] ?? f.defaultValue))
                .map((f) => {
                    const label = f.chipLabel(appliedValues[f.id] ?? f.defaultValue);
                    return label ? { id: f.id, label } : null;
                })
                .filter((chip): chip is AppliedChipData => chip !== null),
        [appliedValues],
    );

    const appliedCount = appliedChips.length;

    const getApplied = useCallback(
        <T>(id: string): T => {
            const filter = getFilterById(id);
            return (appliedValues[id] ?? filter?.defaultValue) as T;
        },
        [appliedValues],
    );

    const getPending = useCallback(
        <T>(id: string): T => {
            const filter = getFilterById(id);
            return (pending[id] ?? filter?.defaultValue) as T;
        },
        [pending],
    );

    const setPending = useCallback((id: string, value: unknown) => {
        setPendingState((prev) => ({ ...prev, [id]: value }));
    }, []);

    // Toggling a filter only changes the pending picker state — it does not
    // commit to applied or the URL. Apply commits; removeApplied is the only
    // single-filter path that touches applied directly.
    const togglePicked = useCallback((id: string) => {
        const filter = getFilterById(id);
        if (!filter) return;

        setPendingState((p) => ({ ...p, [id]: filter.defaultValue }));
        setPicked((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const apply = useCallback(() => {
        const nextApplied: FilterValueMap = {};

        // Merge onto existing params so non-filter params like sort survive.
        setSearchParams(
            (current) => {
                const params = new URLSearchParams(current);
                FILTER_REGISTRY.forEach((f) => {
                    (f.urlKeys ?? [f.urlKey]).forEach((k) => params.delete(k));
                });
                FILTER_REGISTRY.forEach((f) => {
                    if (picked.has(f.id)) {
                        const value = pending[f.id] ?? f.defaultValue;
                        if (f.isApplied(value)) {
                            f.serializeToParams(params, value);
                            nextApplied[f.id] = value;
                        }
                    }
                });
                return params;
            },
            { replace: true },
        );
        setAppliedValues(nextApplied);
    }, [picked, pending, setSearchParams]);

    const reset = useCallback(() => {
        setPicked(new Set());
        setPendingState(buildDefaultMap());
        setAppliedValues({});
        setSearchParams(
            (current) => {
                const params = new URLSearchParams(current);
                FILTER_REGISTRY.forEach((f) => {
                    (f.urlKeys ?? [f.urlKey]).forEach((k) => params.delete(k));
                });
                return params;
            },
            { replace: true },
        );
    }, [setSearchParams]);

    const removeApplied = useCallback(
        (id: string) => {
            const filter = getFilterById(id);
            if (!filter) return;

            setPendingState((p) => ({ ...p, [id]: filter.defaultValue }));
            setAppliedValues((a) => {
                const u = { ...a };
                delete u[id];
                return u;
            });
            setPicked((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            setSearchParams(
                (params) => {
                    const u = new URLSearchParams(params);
                    (filter.urlKeys ?? [filter.urlKey]).forEach((k) => u.delete(k));
                    return u;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    return {
        picked,
        pending,
        applied: appliedValues,
        appliedCount,
        appliedChips,
        getApplied,
        getPending,
        setPending,
        togglePicked,
        apply,
        reset,
        removeApplied,
    };
}
